import React, { useState, useMemo } from "react";
import {
  Card,
  Typography,
  Space,
  Button,
  Upload,
  Popconfirm,
  Modal,
  Row,
  Col,
  Tooltip,
} from "antd";
import { UploadOutlined, WarningOutlined } from "@ant-design/icons";
import UserService from "../../services/UserService";
import DocumentService from "../../services/DocumentService";
import { openNotification } from "../../utils";

const { Title, Text } = Typography;

interface DocumentsTabProps {
  profileData: any;
  setProfileData: (data: any) => void;
}

type DocType = "image" | "pdf" | "other";

const detectFileType = (url: string): DocType => {
  const lower = url.toLowerCase();
  if (
    [".jpg", ".jpeg", ".png", ".gif", ".webp"].some((ext) =>
      lower.includes(ext)
    )
  ) {
    return "image";
  } else if (lower.includes(".pdf")) {
    return "pdf";
  } else {
    return "other";
  }
};

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  profileData,
  setProfileData,
}) => {
  const [identityUploading, setIdentityUploading] = useState(false);
  const [showIdentityUpload, setShowIdentityUpload] = useState(false);
  const [identityPreviewVisible, setIdentityPreviewVisible] = useState(false);
  const [identityPreviewUrl, setIdentityPreviewUrl] = useState<string | null>(
    null
  );
  const [identityPreviewType, setIdentityPreviewType] = useState<DocType | null>(
    null
  );

  // Türü güvenceye al (url/type değişirse eşitle)
  const effectiveType: DocType | null = useMemo(() => {
    if (!identityPreviewUrl) return null;
    if (identityPreviewType === "image" || identityPreviewType === "pdf" || identityPreviewType === "other") {
      return identityPreviewType;
    }
    return detectFileType(identityPreviewUrl);
  }, [identityPreviewUrl, identityPreviewType]);

  // Kimlik kartını sil
  const handleIdentityDelete = async () => {
    try {
      await UserService.updateMe({
        identityDocumentUrl: null,
        identityDocumentType: null,
      });
      openNotification("success", "Başarılı", "Kimlik kartı silindi.");
      setProfileData((prev: any) => ({
        ...prev,
        identityDocumentUrl: undefined,
        identityDocumentType: undefined,
      }));
      setShowIdentityUpload(false);
    } catch (e) {
      console.error(e);
      openNotification("error", "Hata", "Kimlik kartı silinemedi.");
    }
  };

  // Önizleme aç
  const openIdentityPreview = (
    url?: string | null,
    type?: DocType | null
  ) => {
    if (!url) return;
    setIdentityPreviewType(type || detectFileType(url));
    setIdentityPreviewUrl(url);
    setIdentityPreviewVisible(true);
  };

  // Modal içi indir
  const handleDownload = () => {
    if (!identityPreviewUrl) return;
    const a = document.createElement("a");
    a.href = identityPreviewUrl;
    const guessedName =
      identityPreviewUrl.split("/").pop()?.split("?")[0] || "belge";
    a.setAttribute("download", guessedName);
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Upload işlemi
  const handleIdentityUpload = async (file: File) => {
    try {
      setIdentityUploading(true);
      const uploadResponse: any = await DocumentService.postDocument(
        file,
        file.name
      );
      const documentId: any = uploadResponse?.data?.id;
      if (!documentId) throw new Error("Belge yüklenemedi");

      const docRes: any = await DocumentService.getDocument(documentId);
      const url =
        docRes?.data?.preSignUrl ||
        docRes?.data?.url ||
        docRes?.data?.preSignedUrl;
      if (!url) throw new Error("Belge URL alınamadı");

      const type = detectFileType(url);

      await UserService.updateMe({
        identityDocumentUrl: url,
        identityDocumentType: type,
      });
      openNotification("success", "Başarılı", "Kimlik kartı yüklendi.");
      setProfileData((prev: any) => ({
        ...prev,
        identityDocumentUrl: url,
        identityDocumentType: type,
      }));
      setShowIdentityUpload(false);
    } catch (e) {
      console.error(e);
      openNotification("error", "Hata", "Kimlik kartı yüklenemedi.");
    } finally {
      setIdentityUploading(false);
    }
    return false;
  };

  // koşullar
  const notConfirmed =
    !!profileData.identityDocumentUrl && !profileData.identityDocumentConfirmAt;

  return (
    <>
      <Card className="br-30px">
        <Space align="center">
          <Title level={5} style={{ margin: 0 }}>
            Türkiye Cumhuriyeti Kimlik Kartı
          </Title>
          {notConfirmed && (
            <Tooltip title="Kimlik kartınız onaylı değil.">
              <WarningOutlined style={{ color: "#faad14", fontSize: 18 }} />
            </Tooltip>
          )}
        </Space>

        <Space
          direction="vertical"
          size={12}
          style={{ width: "100%", marginTop: 12 }}
        >
          {profileData.identityDocumentUrl ? (
            <Space>
              <Button
                type="default"
                className="d-button"
                onClick={() =>
                  openIdentityPreview(
                    profileData.identityDocumentUrl,
                    profileData.identityDocumentType
                  )
                }
              >
                Görüntüle / İndir
              </Button>
              {/* Sil butonu sadece confirmed değilse görünsün */}
              {notConfirmed && (
                <Popconfirm
                  title="Kimlik kartını silmek istiyor musunuz?"
                  okText="Evet"
                  cancelText="Hayır"
                  placement="topRight"
                  onConfirm={handleIdentityDelete}
                >
                  <Button danger className="d-button">
                    Sil
                  </Button>
                </Popconfirm>
              )}
            </Space>
          ) : (
            <Text>
              Henüz kimlik kartı yüklenmemiş. Lütfen kimlik kartınızı yükleyin.
            </Text>
          )}

          {(showIdentityUpload || !profileData.identityDocumentUrl) && (
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                handleIdentityUpload(file as any);
                return false;
              }}
              accept="image/*,application/pdf"
            >
              <Button loading={identityUploading} icon={<UploadOutlined />}>
                Kimlik Kartı Yükle
              </Button>
            </Upload>
          )}
        </Space>
      </Card>

      <Modal
        title="Kimlik Belgesi"
        open={identityPreviewVisible}
        onCancel={() => setIdentityPreviewVisible(false)}
        footer={null}
        width={900}
        bodyStyle={{ padding: 16 }}
      >
        {identityPreviewUrl ? (
          effectiveType === "image" ? (
            <div style={{ textAlign: "center" }}>
              <img
                src={identityPreviewUrl}
                alt="Türkiye Cumhuriyeti Kimlik Kartı"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            </div>
          ) : (
            <div style={{ textAlign: "center" }}>
              <p>Bu dosya önizlenemiyor. Aşağıdan indirebilirsiniz.</p>
              <Button onClick={handleDownload}>İndir</Button>
            </div>
          )
        ) : (
          <div style={{ textAlign: "center" }}>
            <p>Bu dosya görüntülenemiyor.</p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DocumentsTab;
